/**
 * Client copy of the backend pricing engine (unbeatable-backend-v2
 * services/rateEngine.js). Both run the same vectors
 * (src/utils/__tests__/rateEngine.vectors.json), so a rate shown anywhere in
 * the app matches what the backend quotes.
 *
 *   rate = baseRate + (grid cell - grid anchorRate) + 15-yr term spread
 */

export type RateDimension = 'creditScore' | 'ltv' | 'dti' | 'dscr';

export interface EngineMatrix {
  rowLabels?: string[];
  columnLabels?: string[];
  rowDimension?: RateDimension | null;
  columnDimension?: RateDimension | null;
  anchorRate?: number | null;
  values?: number[][];
}

export interface EngineProgram {
  baseRate?: number | null;
  matrix?: EngineMatrix | null;
  adjustments?: {
    creditScore?: Partial<
      Record<'excellent' | 'good' | 'fair' | 'poor', number>
    >;
    ltv?: Partial<Record<'low' | 'moderate' | 'high' | 'veryHigh', number>>;
  } | null;
}

export interface EngineRateDoc {
  programs?: Record<string, EngineProgram | undefined> | null;
  termSpreads?: { fifteenYear?: number | null } | null;
  metadata?: { termSpreads?: { fifteenYear?: number | null } | null } | null;
}

export interface PricingInputs {
  creditScore?: number | null;
  /** Percent (80) or fraction (0.8). */
  ltv?: number | null;
  /** Percent (36) or fraction (0.36). */
  dti?: number | null;
  dscr?: number | null;
  loanTerm?: number | null;
}

export interface PricedRate {
  program: string;
  rate: number;
  baseRate: number;
  adjustment: number;
  termAdjustment: number;
  source: 'matrix' | 'adjustments' | 'base' | 'cash';
  bucket: { row: string; column: string } | null;
}

const TERM_SPREAD_PROGRAMS = new Set([
  'conventional',
  'fha',
  'va',
  'usda',
  'heloan',
]);
export const DEFAULT_PRICING_INPUTS = {
  creditScore: 740,
  ltv: 80,
  dti: 36,
  dscr: 1.25,
};

const round3 = (n: number) => Math.round(n * 1000) / 1000;
const finite = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : null;

export function bucketLowerBound(label: string): number | null {
  const text = String(label || '');
  const inner = (text.match(/\(([^)]*)\)/) || [null, text])[1] as string;
  if (/[<≤]/.test(inner)) return -Infinity;
  const num = inner.match(/\d+(?:\.\d+)?/);
  return num ? Number(num[0]) : null;
}

export function bucketIndex(
  labels: string[] | undefined,
  value: number | null
): number {
  if (!Array.isArray(labels) || !labels.length || finite(value) == null)
    return -1;
  const bounds = labels
    .map((label, index) => ({ index, lo: bucketLowerBound(label) }))
    .filter((b): b is { index: number; lo: number } => b.lo != null)
    .sort((a, b) => a.lo - b.lo);
  let match = -1;
  for (const b of bounds) {
    if ((value as number) >= b.lo) match = b.index;
  }
  return match === -1 && bounds.length ? bounds[0].index : match;
}

function legacyAdjustment(
  program: EngineProgram,
  creditScore: number,
  ltv: number
): number {
  const cs = program.adjustments?.creditScore || {};
  let adj = 0;
  if (creditScore >= 760) adj += cs.excellent ?? 0;
  else if (creditScore >= 740) adj += cs.good ?? 0;
  else if (creditScore >= 720) adj += cs.fair ?? 0;
  else if (creditScore >= 700) adj += 0;
  else if (creditScore >= 680) adj += 0.125;
  else if (creditScore >= 660) adj += 0.25;
  else if (creditScore >= 640) adj += 0.375;
  else if (creditScore >= 620) adj += 0.5;
  else if (creditScore >= 600) adj += 0.625;
  else if (creditScore >= 580) adj += 0.75;
  else if (creditScore >= 560) adj += 0.875;
  else if (creditScore >= 540) adj += 1.0;
  else if (creditScore >= 520) adj += 1.125;
  else if (creditScore >= 500) adj += 1.25;
  else adj += 2.5;

  const l = program.adjustments?.ltv || {};
  if (ltv <= 60) adj += l.low ?? 0;
  else if (ltv <= 70) adj += l.moderate ?? 0;
  else if (ltv <= 80) adj += l.high ?? 0;
  else if (ltv <= 90) adj += l.veryHigh ?? 0;
  else adj += 0.5;
  return adj;
}

function hasUsableMatrix(
  m: EngineMatrix | null | undefined
): m is Required<EngineMatrix> {
  return !!(
    m &&
    Array.isArray(m.values) &&
    m.values.length > 0 &&
    m.rowDimension &&
    m.columnDimension &&
    finite(m.anchorRate) != null
  );
}

export function normalizePricingInputs(inputs: PricingInputs = {}) {
  const pct = (v: unknown) => {
    const n = finite(Number(v));
    if (n == null || v === null || v === undefined || v === '') return null;
    return n > 0 && n <= 1.5 ? n * 100 : n;
  };
  const num = (v: unknown) =>
    v === null || v === undefined || v === '' ? null : finite(Number(v));
  const creditScore = num(inputs.creditScore);
  return {
    creditScore:
      creditScore != null && creditScore > 0
        ? creditScore
        : DEFAULT_PRICING_INPUTS.creditScore,
    ltv: pct(inputs.ltv) ?? DEFAULT_PRICING_INPUTS.ltv,
    dti: pct(inputs.dti) ?? DEFAULT_PRICING_INPUTS.dti,
    dscr: num(inputs.dscr) ?? DEFAULT_PRICING_INPUTS.dscr,
    loanTerm: num(inputs.loanTerm) ?? 30,
  };
}

/**
 * Prices one program. Returns null (rather than throwing) when the program
 * has no base rate, so callers can fall back.
 */
export function priceProgram(
  rateDoc: EngineRateDoc | null | undefined,
  programKey: string,
  inputs: PricingInputs = {}
): PricedRate | null {
  const used = normalizePricingInputs(inputs);
  if (programKey === 'cash') {
    return {
      program: 'cash',
      rate: 0,
      baseRate: 0,
      adjustment: 0,
      termAdjustment: 0,
      source: 'cash',
      bucket: null,
    };
  }
  const program = rateDoc?.programs?.[programKey];
  const baseRate = finite(program?.baseRate);
  if (!program || baseRate == null) return null;

  let adjustment = 0;
  let source: PricedRate['source'] = 'base';
  let bucket: PricedRate['bucket'] = null;
  const m = program.matrix;
  if (hasUsableMatrix(m)) {
    const r = bucketIndex(m.rowLabels, used[m.rowDimension as RateDimension]);
    const c = bucketIndex(
      m.columnLabels,
      used[m.columnDimension as RateDimension]
    );
    const cell = r >= 0 && c >= 0 ? finite(m.values?.[r]?.[c]) : null;
    if (cell != null) {
      adjustment = cell - (m.anchorRate as number);
      source = 'matrix';
      bucket = { row: m.rowLabels[r], column: m.columnLabels[c] };
    }
  }
  if (source === 'base' && program.adjustments) {
    adjustment = legacyAdjustment(program, used.creditScore, used.ltv);
    source = 'adjustments';
  }

  const spread15 = finite(
    rateDoc?.termSpreads?.fifteenYear ??
      rateDoc?.metadata?.termSpreads?.fifteenYear
  );
  const termAdjustment =
    used.loanTerm <= 20 &&
    spread15 != null &&
    TERM_SPREAD_PROGRAMS.has(programKey)
      ? spread15
      : 0;

  return {
    program: programKey,
    rate: round3(Math.max(0, baseRate + adjustment + termAdjustment)),
    baseRate: round3(baseRate),
    adjustment: round3(adjustment),
    termAdjustment: round3(termAdjustment),
    source,
    bucket,
  };
}

/** Frontend loan-type names -> backend program keys. */
export const LOAN_TYPE_TO_PROGRAM_KEY: Record<string, string> = {
  Cash: 'cash',
  Conventional: 'conventional',
  FHA: 'fha',
  VA: 'va',
  USDA: 'usda',
  DSCR: 'dscr',
  Commercial: 'commercial',
  FixAndFlip: 'fixAndFlip',
  Bridge: 'bridge',
  HELOC: 'heloc',
  HELOAN: 'heloan',
  SecondMortgage: 'secondMortgage',
};
